import {Component, Input, OnInit} from '@angular/core';
import {slideCollapseAnimation} from '../../../utils/slide-collapse.animation';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ListaService} from '../../../_services/lista.service';
import {NavService} from '../../../_services/_nav.service';
import {forkJoin} from 'rxjs';
import {Listas} from '../../../_enums/enum';
import {Producto} from '../../../_models/producto';
import {ProductoAdjunto} from '../../../_models/producto-adjunto';
import {ProductoAdjuntoService} from '../../../_services/producto-adjunto.service';
import {AdministradorDocumentoService} from '../../../_services/administrador-documento.service';

@Component({
  selector: 'app-producto-adjuntos',
  templateUrl: './producto-adjuntos.component.html',
  styleUrl: './producto-adjuntos.component.css',
  animations: [slideCollapseAnimation],
})
export class ProductoAdjuntosComponent implements OnInit {
  @Input() isDetail: boolean = false;
  @Input() producto: Producto;
  isLoading: boolean = true;
  records: ProductoAdjunto[] = [];
  typesLinkages: SelectItem[] = [];
  typesNatures: SelectItem[] = [];
  typesOccupations: SelectItem[] = [];
  attachments: SelectItem[] = [];
  cols: any[] = [];
  rows: number = 10;

  showForm: boolean = false;
  form: FormGroup;
  submitted: boolean = false;
  saving: boolean = false;
  cdKey: string = 'producto-adjuntos';

  constructor(
    private fb: FormBuilder,
    private service: ProductoAdjuntoService,
    private administradorDocumentoService: AdministradorDocumentoService,
    private listService: ListaService,
    private navService: NavService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.cols = [
      { field: 'tiposVinculacionesNames', header: this.navService.buscarMensaje('PRODUCTO_ADJUNTO.LBL_TIPO_VINCULACION'), sort: false },
      { field: 'tiposPersonasNames', header: this.navService.buscarMensaje('PRODUCTO_ADJUNTO.LBL_NATURALEZA'), sort: false },
      { field: 'tiposOcupacionesNames', header: this.navService.buscarMensaje('PRODUCTO_ADJUNTO.LBL_OCUPACION'), sort: false },
      { field: 'productosAdjuntos.nombre', header: this.navService.buscarMensaje('PRODUCTO_ADJUNTO.LBL_ADJUNTO'), sort: false },
    ];

    if(!this.isDetail) {
      this.cols.push({ field: 'acciones', header: this.navService.buscarMensaje('LBL_ACCIONES'), sort: false });
    }

    this.loadData();
  }

  loadData(): void {
    forkJoin([
      this.service.getByIdProductoEnabled(this.producto?.idProducto),
      this.listService.getMasterDetails(Listas.LISTAS_TIPOS_VINCULACIONES),
      this.listService.getMasterDetails(Listas.LISTAS_TIPOS_PERSONAS),
      this.listService.getMasterDetails(Listas.LISTAS_TIPOS_OCUPACIONES),
      this.administradorDocumentoService.getAllEnable(),
    ]).subscribe({
      next: ([records, typesLinkages, typesNatures, typesOccupations, attachments]) => {
        this.records = records;
        this.typesLinkages = typesLinkages.map(e => ({label: e.nombre, value: e.idLista}));
        this.typesNatures = typesNatures.map(e => ({label: e.nombre, value: e.idLista}));
        this.typesOccupations = typesOccupations.map(e => ({label: e.nombre, value: e.idLista}));
        this.attachments = attachments.map(e => ({label: e.nombre, value: e.idDocumentoTercero}));
        this.isLoading = false;
      },
    });
  }

  loadRecords(): void {
    this.isLoading = true;
    this.service.getByIdProductoEnabled(this.producto?.idProducto).subscribe({
      next: (records) => {
        this.records = records;
        this.isLoading = false;
      },
    });
  }

  onCreate(): void {
    this.buildForm();
  }

  onDelete(record: ProductoAdjunto): void {
    this.confirmationService.confirm({
      key: this.cdKey,
      header: this.navService.buscarMensaje('LBL_CONFIRMACION'),
      message: this.navService.buscarMensaje('MSG_ELIMINARCAMBIOS'),
      accept: () => {
        this.saving = true;
        record.productosAdjuntos.indicadorHabilitado = false;
        this.service.save(record).subscribe({
          next: () => {
            this.navService.setMesage(2);
            this.saving = false;
            this.loadRecords();
          },
          error: () => {
            this.navService.setMesage(3);
            this.saving = false;
          },
        });
      },
    });
  }

  onCancel(): void {
    if(this.form?.dirty) {
      this.confirmationService.confirm({
        key: this.cdKey,
        header: this.navService.buscarMensaje('LBL_CAMBIOS_SIN_GUARDAR'),
        message: this.navService.buscarMensaje('MSG_CAMBIOS_SIN_GUARDAR'),
        accept: () => this.showForm = false,
      });
    } else {
      this.showForm = false;
    }
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.form.invalid) return;

    this.saving = true;
    const value = this.form.value;
    const record: ProductoAdjunto = {
      productosAdjuntos: {
        idProducto: this.producto.idProducto,
        idDocumentoTercero: value.idDocumentoTercero,
        indicadorHabilitado: true,
      },
      tiposVinculaciones: value.idsTiposVinculaciones.map(id => ({ idTipoVinculacion: id })),
      tiposOcupaciones: value.idsTiposOcupaciones.map(id => ({ idTipoOcupacion: id })),
      tiposPersonas: value.idsTiposPersonas.map(id => ({ idTipoPersona: id })),
    } as ProductoAdjunto;
    this.service.save(record).subscribe({
      next: () => {
        this.navService.setMesage(1);
        this.saving = false;
        this.showForm = false;
        this.loadRecords();
      },
      error: () => {
        this.navService.setMesage(3);
        this.saving = false;
      },
    });
  }

  get f() {
    return this.form.controls;
  }

  private buildForm(): void {
    this.form = this.fb.group({
      idsTiposVinculaciones: [null, [Validators.required]],
      idsTiposPersonas: [null, [Validators.required]],
      idsTiposOcupaciones: [null, [Validators.required]],
      idDocumentoTercero: [null, [Validators.required]],
    });
    this.submitted = false;
    this.showForm = true;
  }
}
